import { Box, Fade, Grid, Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { HIDDEN_CLASS, NO_OP_CALLBACK } from "@smartrr/shared/constants";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusButtonGroup } from "@smartrr/shared/customerPortal/components/AquariusButtonGroup";
import { AquariusIconButton } from "@smartrr/shared/customerPortal/components/AquariusIconButton";
import { IGift } from "@smartrr/shared/entities/Gift";
import { IOrganization } from "@smartrr/shared/entities/Organization";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { SellingPlanConfigs } from "@smartrr/shared/entities/SellingPlanGroup";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { getNextActualDeliveryForCpsGroup } from "@smartrr/shared/utils/getScheduledDeliveries";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useToggleModal } from "@customer-app/state/actionCreators/modal";
import { useSellingPlanGroupForSellingPlanId } from "@customer-app/state/reducers/sellingPlanGroups";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";
import { useSendOrderNowAvailability } from "@customer-app/utils/useSendOrderNowAvailability";

import { DefaultView } from "./components/DefaultView";
import { DelayDateView } from "./components/DelayDateView";
import { EditAddressView } from "./components/EditAddressView";
import { GiftView } from "./components/GiftView";
import { OrderNowView } from "./components/OrderNowView";
import { SkipView } from "./components/SkipView";
import { SwapItemView } from "./components/SwapItemView";
import { SwapPlanView } from "./components/SwapPlanView";
import { ACTIVE_CLASS, ButtonGrid, CENTERED_CLASS, SELECTED_CLASS } from "./constants";
import { useModalButtonOptions } from "./ManageSubscriptionsModal/hooks/useModalButtonOptions";
import { useSmartrrTheme } from "../../../../hooks/useSmartrrTheme";
import { useMobileQuery } from "../../../../utils";
import { getFormattedCustomerSupportEmail } from "../utils";
import { DeliveryId, getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";
import { DeliveryAccess } from "@customer-app/state/stores/delivery";

//ValidViews Type
export declare type ValidViews =
  | "Order now"
  | "Skip"
  | "Gift"
  | "Swap item"
  | "Delay date"
  | "Edit address"
  | "Default"
  | "Swap Plan"
  | "Edit frequency"
  | "Set next date"
  | "Edit payment"
  | "Pause"
  | "Resume"
  | "Cancel"
  | "NullView";

export interface IOrderOptionsModalInnerProps {
  deliveryId: DeliveryId;
  delayDateAvailable: boolean;
  gift?: IGift;
  isSellingPlanDeleted: boolean;
  purchasablesForAdding: IPurchasable[];
  organization: IOrganization;
  initialView?: ValidViews;
  initialLineItemId?: string;
}

interface IOrderOptionsModalInnerContentProps extends Omit<IOrderOptionsModalInnerProps, "deliveryId"> {
  delivery: PurchaseStateAndDelivery;
}

export const OrderOptionsModalInner = ({ deliveryId, ...props }: IOrderOptionsModalInnerProps): JSX.Element => {
  const delivery = DeliveryAccess.useDelivery(deliveryId);

  if (!delivery) {
    return <React.Fragment />;
  }

  return <OrderOptionsModalInnerContent {...props} delivery={delivery} />;
};

const OrderOptionsModalInnerContent = ({
  delivery,
  gift,
  isSellingPlanDeleted,
  purchasablesForAdding,
  delayDateAvailable,
  organization,
  initialView,
  initialLineItemId,
}: IOrderOptionsModalInnerContentProps): JSX.Element => {
  // Since we are rendering this modal through redux, we need to get the most up to date purchase state opposed to the one passed in
  const purchaseState = useSmartrrCustomerSelector(state => state.purchaseStates.purchaseStates).find(
    ps => ps.id === delivery.purchaseState.id
  )!;

  const theme = useSmartrrTheme();
  // if no original delivery (if skipped or paused) getting next delivery for purchaseState
  delivery = delivery ?? getNextActualDeliveryForCpsGroup(organization, [purchaseState])?.[1][0]!;

  const isMobile = useMobileQuery();
  const localizeShorthand = "subscriptions.yourSubscriptions.orderOptionsModal";

  const [view, setView] = useState<ValidViews>(initialView ?? "Default");
  const sendNowAvailable = useSendOrderNowAvailability(delivery);

  const themeConditionalActions = theme.modernThemeSettings?.nextOrderActions;
  const customerShopifyId = shopifyGidToNumber(purchaseState.pmtMth.paymentMethodInternalCustomerId);
  const sellingPlanIdNumber = purchaseState?.sellingPlanId
    ? shopifyGidToNumber(purchaseState?.sellingPlanId)
    : null;
  const sellingPlanOptions = useSellingPlanGroupForSellingPlanId(sellingPlanIdNumber);

  // SO MANY REFS!!!
  const buttonGridRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const buttonOptionsRef = useRef<HTMLDivElement[]>([]);

  const isPrepaid =
    purchaseState.schedule.paymentFrequencyMultiple !== purchaseState.schedule.deliveryFrequencyMultiple;

  const buttonOptions = useModalButtonOptions(delivery, "orderOptions");

  const areThemeActionsAvailable = Object.values(themeConditionalActions!).some(item => item);

  useEffect(() => {
    buttonOptionsRef.current = buttonOptionsRef.current.slice(0, buttonOptions.length);
  }, [buttonOptions]);

  useEffect(() => {
    if (view !== "Default" && buttonOptionsRef.current && buttonOptions) {
      setIconButtonActive(buttonOptions.findIndex(ele => ele?.view === view));
    }
  }, [buttonOptionsRef.current, buttonOptions.length]);

  const toggleModal = useToggleModal();

  const returnToOrderOptions = useCallback(() => {
    toggleModal("ORDER_OPTIONS", {
      deliveryId: getDeliveryId(delivery),
      delayDateAvailable,
      gift,
      isSellingPlanDeleted,
      purchasablesForAdding,
      organization,
    });
  }, [
    delayDateAvailable,
    delivery,
    gift,
    isSellingPlanDeleted,
    organization,
    purchasablesForAdding,
    sendNowAvailable,
    theme,
  ]);

  const openAddonList = useCallback(() => {
    toggleModal("ADDON_LIST", {
      deliveryId: getDeliveryId(delivery),
      purchasables: purchasablesForAdding,
      title: typedLocalize("subscriptions.addToYourNextOrder.modalTitle"),
      configType: SellingPlanConfigs.ADDON,
      onCancel: returnToOrderOptions,
    });
  }, []);

  const setIconButtonActive = useCallback(
    index => {
      for (const [i, e] of buttonOptions.entries()) {
        const ele = buttonOptionsRef.current[i];
        if (ele) {
          const left = ele.offsetLeft ?? 0;
          /**
           * The two setTimeouts are needed as the offsetLeft lookup happens AFTER the if statement for some reason
           * resulting in all of the buttons not staying in their original positions before moving to center
           */

          setTimeout(() => {
            if (index === i) {
              setView(e?.view!);

              if (!boxRef.current || !buttonGridRef!.current) {
                return;
              }

              if (isMobile) {
                buttonGridRef!.current!.classList.toggle("just-center");
                ele.classList.add(CENTERED_CLASS);
              } else {
                ele.classList.add(SELECTED_CLASS);
                ele.style.left = `${left}px`; // this is needed so the animation is smooth (old position -> new left position)
                boxRef!.current!.style.height =
                  ele.getBoundingClientRect().height < 90
                    ? "90px"
                    : ele.getBoundingClientRect().height + 10 + "px";
                setTimeout(() => {
                  ele.classList.add(ACTIVE_CLASS);
                }, 500);
              }
            } else {
              ele.classList.add(HIDDEN_CLASS); // hiding all other buttons
            }
          }, 0);
        }
      }
    },
    [buttonOptionsRef, buttonOptions, buttonGridRef, boxRef]
  );

  const setIconButtonInactive = useCallback(() => {
    if (!delivery.isSkipped || !gift) {
      for (const [i, e] of buttonOptions.entries()) {
        const ele = buttonOptionsRef.current[i];
        if (ele) {
          if (ele.classList.contains(ACTIVE_CLASS) || ele.classList.contains(CENTERED_CLASS)) {
            if (isMobile) {
              buttonGridRef!.current!.classList.remove("just-center");
              ele.classList.remove(CENTERED_CLASS);
            } else {
              ele.classList.remove(ACTIVE_CLASS);
              setTimeout(() => {
                ele.classList.remove(SELECTED_CLASS);
              }, 1000);
            }
          }
        }
      }

      setTimeout(
        () => {
          for (const [i, e] of buttonOptions.entries()) {
            const ele = buttonOptionsRef.current[i];
            ele.classList.remove(HIDDEN_CLASS);
          }
        },
        isMobile ? 0 : 1000
      );

      setTimeout(
        () => {
          setView("Default");

          if (!boxRef.current) {
            return;
          }

          boxRef.current.style.height = "max-content";
        },
        isMobile ? 0 : 1000
      );
    } else {
      setTimeout(() => {
        setView("Default");
      }, 500);
    }
  }, [buttonOptions]);

  const ViewComponent = () => {
    switch (view) {
      case "Default": {
        return (
          <Fade in={view === "Default"}>
            <div>
              <DefaultView
                delivery={delivery}
                openAddonList={purchasablesForAdding.length ? openAddonList : NO_OP_CALLBACK}
                gift={gift}
                isSellingPlanDeleted={isSellingPlanDeleted}
                isPrepaid={isPrepaid}
                purchasablesForAdding={purchasablesForAdding}
                returnToModal={returnToOrderOptions}
              />
            </div>
          </Fade>
        );
      }
      case "Order now": {
        return (
          <Fade in={view === "Order now"}>
            <div>
              <OrderNowView setIconButtonInactive={setIconButtonInactive} purchaseState={purchaseState} />
            </div>
          </Fade>
        );
      }
      case "Skip": {
        return (
          <Fade in={view === "Skip"}>
            <div>
              <SkipView setIconButtonInactive={setIconButtonInactive} delivery={delivery} />
            </div>
          </Fade>
        );
      }
      case "Gift": {
        return (
          <Fade in={view === "Gift"}>
            <div>
              <GiftView delivery={delivery} onCancel={setIconButtonInactive} onConfirm={NO_OP_CALLBACK} />
            </div>
          </Fade>
        );
      }
      case "Swap item": {
        return (
          <Fade in={view === "Swap item"}>
            <div>
              <SwapItemView
                setIconButtonInactive={setIconButtonInactive}
                delivery={delivery}
                initialLineItemId={initialLineItemId}
              />
            </div>
          </Fade>
        );
      }
      case "Delay date": {
        return (
          <Fade in={view === "Delay date"}>
            <div>
              <DelayDateView setIconButtonInactive={setIconButtonInactive} delivery={delivery} />
            </div>
          </Fade>
        );
      }
      case "Edit address": {
        return (
          <Fade in={view === "Edit address"}>
            <div>
              <EditAddressView setIconButtonInactive={setIconButtonInactive} delivery={delivery} gift={gift} />
            </div>
          </Fade>
        );
      }
      case "Swap Plan": {
        return (
          <Fade in={view === "Swap Plan"}>
            <div>
              <SwapPlanView setView={setView} delivery={delivery} sellingPlanOptions={sellingPlanOptions} />
            </div>
          </Fade>
        );
      }
      default: {
        // Should hit here
        return <div></div>;
      }
    }
  };

  return (
    <React.Fragment>
      {!isSellingPlanDeleted && view !== "Swap Plan" && areThemeActionsAvailable ? (
        <Box
          ref={boxRef}
          className={prefixClassName("modal-actions-wrapper")}
          id={"order-options-actions"}
          sx={{
            borderBottom: `1px solid ${theme.modernLineDividers}`,
            transition: ".3s ease-in-out",
            height: "max-content",
            margin: isMobile ? "auto -20px" : "0",
          }}
        >
          <ButtonGrid
            container
            spacing={0}
            ref={buttonGridRef}
            justifyContent={isMobile ? "inherit" : "space-around"}
            direction="row"
            isMobile={isMobile}
          >
            {buttonOptions.map((ele, index) => {
              return (
                <Grid
                  item
                  key={index}
                  ref={el => (buttonOptionsRef.current[index] = el!)}
                  onClick={() => setIconButtonActive(index)}
                  id={`modal-action-${ele?.label}`}
                >
                  <AquariusIconButton
                    iconName={ele?.iconName!}
                    size="large"
                    label={localize(`customerActions.${ele?.label}`)}
                    isActive={view !== "Default"}
                  />
                </Grid>
              );
            })}
          </ButtonGrid>
        </Box>
      ) : isSellingPlanDeleted && view !== "Swap Plan" ? (
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          padding="30px 20px"
        >
          <Typography variant="h3">{localize(`${localizeShorthand}.subPlanDeletedHeader`)}</Typography>
          <Typography variant="body1">{localize(`${localizeShorthand}.subPlanDeletedBody`)}</Typography>
        </Stack>
      ) : null}

      <ViewComponent />
      {isSellingPlanDeleted && view !== "Swap Plan" ? (
        <React.Fragment>
          {sellingPlanOptions.length ? (
            <AquariusButtonGroup topBorder>
              <AquariusButton
                sx={{ marginTop: 0 }}
                href={getFormattedCustomerSupportEmail(organization, customerShopifyId)}
              >
                {typedLocalize(`general.contactSupportBtn`)}
              </AquariusButton>
              <AquariusButton filled onClick={() => setView("Swap Plan")}>
                {localize(`subscriptions.yourSubscriptions.orderOptionsModal.changePlanBtn`)}
              </AquariusButton>
            </AquariusButtonGroup>
          ) : (
            <AquariusButton
              sx={{ marginTop: "15px" }}
              href={getFormattedCustomerSupportEmail(organization, customerShopifyId)}
            >
              {typedLocalize(`general.contactSupportBtn`)}
            </AquariusButton>
          )}
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};
